<h2 mat-dialog-title>{{ c?.type === 'channel' ? 'Group details' : 'Conversation details' }}</h2>

<mat-dialog-content *ngIf="c as conv">
  <!--
    The hero, as WhatsApp's group info: the photo (with a camera badge to change it, for those who manage the group),
    the name (a pencil opens the name and description for editing), what kind of group, how many, and the description.
  -->
  <section class="hero">
    <div class="hero-photo">
      <span class="shot" [style.--hue]="faceHue({ principalId: conv.id })">
        <img *ngIf="heroPhoto as src; else heroIcon" [src]="src" alt="" />
        <ng-template #heroIcon><mat-icon>{{ heroIconName }}</mat-icon></ng-template>
      </span>
      <ng-container *ngIf="conv.type === 'channel' && canManage && !conv.archivedAt">
        <input #photoPicker type="file" accept="image/png,image/jpeg,image/webp" hidden (change)="pickPhoto(photoPicker)" />
        <button type="button" class="photo-badge" (click)="photoPicker.click()" [disabled]="busy"
          [matTooltip]="groupPhoto ? 'Change the group photo' : 'Add a group photo'" [attr.aria-label]="groupPhoto ? 'Change the group photo' : 'Add a group photo'">
          <mat-icon>photo_camera</mat-icon>
        </button>
      </ng-container>
    </div>
    <button type="button" class="link" *ngIf="conv.type === 'channel' && canManage && groupPhoto && !conv.archivedAt" (click)="removePhoto()" [disabled]="busy">Remove photo</button>

    <ng-container *ngIf="!editing; else editForm">
      <div class="hero-name">
        <span>{{ heroTitle }}</span>
        <button mat-icon-button class="edit" *ngIf="conv.type === 'channel' && canManage && !conv.archivedAt" (click)="startEdit()"
          matTooltip="Edit name and description" aria-label="Edit name and description"><mat-icon>edit</mat-icon></button>
      </div>
      <div class="hero-sub">{{ heroSubtitle }}</div>
      <p class="hero-description" *ngIf="conv.description">{{ conv.description }}</p>
      <button type="button" class="link" *ngIf="conv.type === 'channel' && canManage && !conv.description && !conv.archivedAt" (click)="startEdit()">Add a description</button>
    </ng-container>
    <ng-template #editForm>
      <div class="edit-form">
        <mat-form-field appearance="outline" class="full" subscriptSizing="dynamic">
          <mat-label>Group name</mat-label>
          <input matInput [(ngModel)]="name" maxlength="80" cdkFocusInitial />
        </mat-form-field>
        <mat-form-field appearance="outline" class="full" subscriptSizing="dynamic">
          <mat-label>Description</mat-label>
          <textarea matInput [(ngModel)]="description" maxlength="500" rows="2" cdkTextareaAutosize cdkAutosizeMaxRows="5"></textarea>
        </mat-form-field>
        <div class="edit-actions">
          <button mat-button type="button" (click)="cancelEdit()">Cancel</button>
          <button mat-flat-button color="primary" type="button" (click)="saveDetails()"
            [disabled]="busy || !name.trim() || (name === conv.name && description === (conv.description ?? ''))">Save</button>
        </div>
      </div>
    </ng-template>
  </section>

  <p class="error" *ngIf="error">{{ error }}</p>

  <!-- Which messages notify you, and mute: one card. -->
  <section class="card" *ngIf="conv.access === 'participant'">
    <h4 class="card-title">Notifications</h4>
    <button type="button" class="row" *ngFor="let o of notifyOptions" [class.selected]="(conv.notifyLevel || 'all') === o.level"
      (click)="setNotifyLevel(o.level)" [disabled]="busy" role="radio" [attr.aria-checked]="(conv.notifyLevel || 'all') === o.level">
      <mat-icon>{{ (conv.notifyLevel || 'all') === o.level ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
      <span class="row-text">{{ o.label }}</span>
    </button>
    <div class="divider"></div>
    <button type="button" class="row" *ngIf="canMute" (click)="toggleMute()" [disabled]="busy">
      <mat-icon>{{ muted ? 'notifications_active' : 'notifications_off' }}</mat-icon>
      <span class="row-text">{{ muted ? 'Unmute' : 'Mute for a week' }}<small *ngIf="muted && conv.mutedUntil">Muted until {{ conv.mutedUntil | date: 'd MMM, HH:mm' }}</small></span>
    </button>
  </section>

  <section class="card">
    <h4 class="card-title">{{ members.length }} {{ members.length === 1 ? 'member' : 'members' }}</h4>
    <button type="button" class="row add" *ngIf="canManage && !conv.archivedAt && !adding" (click)="startAdding()">
      <span class="row-icon"><mat-icon>person_add</mat-icon></span>
      <span class="row-text">Add people</span>
    </button>

    <div class="adding" *ngIf="adding">
      <mat-form-field appearance="outline" class="full" subscriptSizing="dynamic">
        <mat-label>Add people</mat-label>
        <mat-select [(ngModel)]="toAddIds" multiple placeholder="Choose people">
          <mat-option *ngFor="let p of candidates" [value]="p.principalId">{{ p.name }}</mat-option>
        </mat-select>
        <mat-hint *ngIf="!candidates.length">Everyone in this practice is already here.</mat-hint>
      </mat-form-field>
      <div class="edit-actions">
        <button mat-button (click)="cancelAdding()">Cancel</button>
        <button mat-flat-button color="primary" (click)="addSelected()" [disabled]="busy || !toAddIds.length">Add</button>
      </div>
    </div>

    <div class="loading" *ngIf="loading"><mat-spinner diameter="24"></mat-spinner></div>
    <div class="member" *ngFor="let m of members">
      <span class="face" [style.--hue]="faceHue(m)">
        <img *ngIf="facePhoto(m) as src; else memberLetters" [src]="src" alt="" loading="lazy" />
        <ng-template #memberLetters>{{ faceInitials(m) }}</ng-template>
        <span class="presence" [class.on]="m.online" *ngIf="m.principalId !== me"></span>
      </span>
      <span class="name">{{ m.principalId === me ? 'You' : m.name }}</span>
      <span class="role" *ngIf="m.role !== 'member'">{{ m.role }}</span>
      <button mat-icon-button class="remove" *ngIf="canManage && conv.type !== 'direct' && m.principalId !== me && !conv.archivedAt" (click)="removeMember(m)"
        [matTooltip]="'Remove ' + m.name" [attr.aria-label]="'Remove ' + m.name"><mat-icon>person_remove</mat-icon></button>
    </div>
  </section>

  <!-- Leaving, archiving and deleting: rows, the ones that cannot be undone in red. -->
  <section class="card" *ngIf="hasActions">
    <button type="button" class="row" *ngIf="conv.type === 'channel' && canManage" (click)="archive(!conv.archivedAt)" [disabled]="busy">
      <mat-icon>{{ conv.archivedAt ? 'unarchive' : 'inventory_2' }}</mat-icon>
      <span class="row-text">{{ conv.archivedAt ? 'Unarchive group' : 'Archive group' }}<small>{{ conv.archivedAt ? 'Members can write in it again.' : 'Read-only for everyone; it can be unarchived.' }}</small></span>
    </button>
    <button type="button" class="row danger" *ngIf="conv.access === 'participant' && conv.type !== 'direct'" (click)="leave()" [disabled]="busy">
      <mat-icon>logout</mat-icon>
      <span class="row-text">Leave {{ conv.type === 'channel' ? 'group' : 'conversation' }}</span>
    </button>
    <button type="button" class="row danger" *ngIf="conv.type === 'channel' && data.isOrgAdmin" (click)="deleteChannel()" [disabled]="busy">
      <mat-icon>delete</mat-icon>
      <span class="row-text">Delete group</span>
    </button>
  </section>
</mat-dialog-content>
